import React, { useEffect, useState } from 'react';
import { AreaChart, Donut, Sparkline, COLORS, prefersReducedMotion } from './charts';
import './ModuleDemos.css';

// Live demo widgets for the Landing "Platform modules" strip: each module
// card renders a small animated synthetic-data visual instead of a static
// screenshot — nothing to re-shoot as the product evolves. All loops pause
// under prefers-reduced-motion.

function useTick(ms, length) {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (prefersReducedMotion()) return undefined;
    const t = setInterval(() => setI((v) => (v + 1) % length), ms);
    return () => clearInterval(t);
  }, [ms, length]);
  return i;
}

// ---- agentOS: manifest, then propose → gate → commit ---------------------------
const OS_CAPS = [
  ['erp.post', 'allowed'],
  ['admin.bypass', 'refused'],
];

const OS_PHASES = [
  { stage: 0, caption: 'Manifest lists erp.post. admin.bypass is not callable.' },
  { stage: 1, caption: 'The model proposes Acme · $12,480 · due Oct 14.' },
  { stage: 2, caption: 'Over $10,000. The gate holds. The model cannot post.' },
  { stage: 3, caption: 'A person approves. Only the runtime commits.' },
];

function AgentOsDemo() {
  const tick = useTick(1700, OS_PHASES.length);
  const phase = OS_PHASES[tick];
  const steps = ['Propose', 'Gate', 'Commit'];
  return (
    <div className="md-os">
      <div className="md-os-caps">
        {OS_CAPS.map(([name, st]) => (
          <span key={name} className={`md-os-cap ${st}`}>{name}</span>
        ))}
      </div>
      <div className="md-os-rail" aria-hidden="true">
        <i className="md-os-track" />
        <i className="md-os-bead" data-stage={phase.stage} />
        {steps.map((label, i) => (
          <span key={label} className={`md-os-step s${i} ${phase.stage === i + 1 ? 'on' : ''} ${phase.stage > i + 1 ? 'done' : ''}`}>
            {label}
          </span>
        ))}
      </div>
      <div className={`md-os-slip stage-${phase.stage}`}>
        <strong>INV-20841</strong>
        <em>{phase.stage === 0 ? 'waiting' : phase.stage === 2 ? 'held' : phase.stage === 3 ? 'committed' : 'proposed'}</em>
      </div>
      <div className="md-nq-cap" key={tick}>{phase.caption}</div>
    </div>
  );
}

// ---- Agent IO: the definition, toggles, token -----------------------------------
const IO_TOGGLES = [
  ['read · project', true],
  ['read · pipeline', true],
  ['propose · pipeline', true],
  ['propose · workload', false],
];

function AgentIoDemo() {
  const tick = useTick(1400, IO_TOGGLES.length + 1);
  return (
    <div className="md-io">
      <div className="md-io-name">support-intake</div>
      <div className="md-io-prompt">Answer from projects and pipelines. Creates wait for a person.</div>
      <div className="md-io-toggles">
        {IO_TOGGLES.map(([label, on], i) => (
          <span key={label} className={`md-io-tog ${on && i < tick ? 'on' : ''}`}>{label}</span>
        ))}
      </div>
      <div className={`md-io-token ${tick >= IO_TOGGLES.length ? 'in' : ''}`}>
        script · iframe · API
      </div>
    </div>
  );
}

// ---- nventr Agent: the embed, propose, confirm ----------------------------------
const AG_LINES = [
  { who: 'you', text: 'Set up a pipeline for vendor invoices.' },
  { who: 'agent', text: 'I can propose it. I can’t create it until you confirm.' },
  { who: 'card', text: 'pipeline · invoice-intake' },
  { who: 'done', text: 'Confirmed. The record exists. Design it in nQube.' },
];

function NventrAgentDemo() {
  const tick = useTick(1800, 2);
  const confirmed = tick === 1;
  return (
    <div className="md-widget">
      <div className="md-widget-bar">nventr Agent · support-intake</div>
      {AG_LINES.filter((line) => line.who !== 'done' || confirmed).map((line) => (
        <div key={line.text} className={`md-bubble ${line.who}`}>
          {line.who === 'card' ? (
            <>
              <span>Propose · {line.text}</span>
              <b className={confirmed ? 'yes' : ''}>{confirmed ? '✓ confirmed' : 'Confirm'}</b>
            </>
          ) : line.text}
        </div>
      ))}
    </div>
  );
}

// ---- Listen: data in ------------------------------------------------------------
const LISTEN_SOURCES = [
  ['postgres', 'orders_prod'],
  ['s3', 'archive-2026'],
  ['api', 'salesforce'],
  ['files', 'claims batch'],
];

function ListenDemo() {
  const tick = useTick(1600, LISTEN_SOURCES.length);
  return (
    <div className="md-listen">
      {LISTEN_SOURCES.map(([kind, name], i) => (
        <div key={kind} className={`md-src ${i === tick ? 'sync' : ''}`}>
          <span className="md-src-kind">{kind}</span>
          <span className="md-src-name">{name}</span>
          <em>{i === tick ? '⇄ syncing' : '✓ synced'}</em>
        </div>
      ))}
      <div className="md-listen-foot">12 datasets · 4 live connections</div>
    </div>
  );
}

// ---- Reachout: alerts + data out ------------------------------------------------
const REACHOUT_EVENTS = [
  { agent: 'slack #ops', action: 'alert · error rate 2.1%', verdict: 'commit' },
  { agent: 'email finance@', action: 'invoice INV-20841 posted', verdict: 'commit' },
  { agent: 'webhook erp', action: 'payload · 214 rows', verdict: 'commit' },
  { agent: 'sms on-call', action: 'gate pending > $10k', verdict: 'gate' },
];

function ReachoutDemo() {
  const tick = useTick(1700, REACHOUT_EVENTS.length);
  const rows = [0, 1, 2].map((o) => REACHOUT_EVENTS[(tick + o) % REACHOUT_EVENTS.length]);
  return (
    <div className="md-ticker">
      {rows.map((e, i) => (
        <div key={`${tick}-${i}`} className="md-ticker-row" style={{ animationDelay: `${i * 60}ms` }}>
          <span className="md-agent">{e.agent}</span>
          <span className="md-action">{e.action}</span>
          <span className={`md-verdict ${e.verdict}`}>{e.verdict === 'commit' ? '✓ delivered' : '⏸ queued'}</span>
        </div>
      ))}
    </div>
  );
}

// ---- Athena: mini analytics ----------------------------------------------------
const ATH_POINTS = Array.from({ length: 18 }, (_, i) => ({
  x: i,
  y: 6 + Math.round(4 * Math.sin(i / 2.2) + 2.4 * Math.sin(i / 5) + (i % 3)),
}));

function AthenaDemo() {
  return (
    <div className="md-athena">
      <AreaChart
        height={120}
        series={[{ name: 'runs', color: COLORS.teal, points: ATH_POINTS }]}
        formatX={() => ''}
      />
      <Donut
        size={104}
        thickness={12}
        centerLabel="runs"
        slices={[
          { label: 'done', value: 82, color: COLORS.green },
          { label: 'gated', value: 11, color: COLORS.amber },
          { label: 'error', value: 7, color: COLORS.red },
        ]}
      />
    </div>
  );
}

// ---- Dev Console: plan, diff, preview, deploy on /dev ---------------------------
const DEV_STEPS = [
  ['Plan', 'gate the $10k hold'],
  ['Diff', 'gate.js +18 −2'],
  ['Preview', 'invoice form'],
  ['Deploy', 'UAT, then production'],
];

function DevConsoleDemo() {
  const tick = useTick(1400, DEV_STEPS.length);
  return (
    <div className="md-devops">
      {DEV_STEPS.map(([name, detail], i) => (
        <div key={name} className="md-svc">
          <i className={`md-dot ${i <= tick ? '' : 'wait'}`} />
          <span>{name}</span>
          <em>{i <= tick ? detail : 'waiting'}</em>
        </div>
      ))}
    </div>
  );
}

// ---- DevOps: fleet + feature health -------------------------------------------
const UPTIME = [99.2, 99.6, 99.4, 99.9, 99.7, 99.9, 100, 99.8, 99.9, 100, 99.9, 100];

function CloudOpsDemo() {
  return (
    <div className="md-devops">
      {[['api-gateway', 'ok'], ['agent-runtime', 'ok'], ['pipeline-workers', 'ok']].map(([name, st]) => (
        <div key={name} className="md-svc">
          <i className={`md-dot ${st}`} />
          <span>{name}</span>
          <em>live</em>
        </div>
      ))}
      <div className="md-uptime">
        <Sparkline points={UPTIME.map((v) => v - 99)} width={150} height={26} color={COLORS.green} />
        <span>99.98% · 30d</span>
      </div>
    </div>
  );
}

// ---- IDP: document extraction ---------------------------------------------------
const IDP_FIELDS = [
  ['vendor', 'Acme Industrial'],
  ['invoice #', 'INV-20841'],
  ['amount', '$12,480.00'],
  ['due', 'Oct 14, 2026'],
];

function IdpDemo() {
  const step = useTick(1500, IDP_FIELDS.length + 2);
  return (
    <div className="md-idp">
      <div className="md-doc">
        {[70, 92, 58, 84, 40, 76].map((w, i) => (
          <div key={i} className={`md-doc-line ${i === (step % 6) ? 'scan' : ''}`} style={{ width: `${w}%` }} />
        ))}
      </div>
      <div className="md-fields">
        {IDP_FIELDS.map(([k, v], i) => (
          <div key={k} className={`md-field ${i < step ? 'in' : ''}`}>
            <span>{k}</span>
            <strong>{v}</strong>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- nQube: workflow graph -------------------------------------------------------
// A run pulses through a small pipeline each cycle: ingest → (agent extract ∥
// model classify) → gate → commit. Nodes light as the run reaches them; the
// whole graph resets when the loop restarts.
const NQ_NODES = [
  { id: 'in', label: 'ingest', x: 6, y: 60, w: 60, stage: 0 },
  { id: 'ex', label: 'agent · extract', x: 94, y: 16, w: 88, stage: 1 },
  { id: 'cl', label: 'model · classify', x: 94, y: 104, w: 88, stage: 1 },
  { id: 'gt', label: 'gate', x: 210, y: 60, w: 48, stage: 2, kind: 'gate' },
  { id: 'cm', label: 'commit · ERP', x: 286, y: 60, w: 78, stage: 3 },
];

const NQ_EDGES = [
  { d: 'M66 70 C 80 70, 80 31, 94 31', stage: 1 },
  { d: 'M66 80 C 80 80, 80 119, 94 119', stage: 1 },
  { d: 'M182 31 C 196 31, 196 70, 210 70', stage: 2 },
  { d: 'M182 119 C 196 119, 196 80, 210 80', stage: 2 },
  { d: 'M258 75 L 286 75', stage: 3 },
];

const NQ_CAPTIONS = [
  'run start · 3 documents in',
  'agent extracts · model classifies — same workflow',
  'gate holds anything > $10k for approval',
  'only the runtime commits to the ERP',
  '✓ committed · audit logged · run complete',
];

function NqubeDemo() {
  const stage = useTick(1400, NQ_CAPTIONS.length);
  return (
    <div className="md-nq">
      <svg viewBox="0 0 370 150" className="md-nq-svg" aria-hidden="true">
        {NQ_EDGES.map((e, i) => (
          <path key={i} d={e.d} className={`md-nq-edge ${stage >= e.stage ? 'on' : ''}`} />
        ))}
        {NQ_NODES.map((n) => {
          const on = stage >= n.stage;
          const ok = n.id === 'cm' && stage >= 4;
          return (
            <g key={n.id} className={`md-nq-node ${n.kind || ''} ${on ? 'on' : ''} ${ok ? 'ok' : ''}`}>
              <rect x={n.x} y={n.y} width={n.w} height={30} rx={8} />
              <text x={n.x + n.w / 2} y={n.y + 19}>{ok ? '✓ committed' : n.label}</text>
            </g>
          );
        })}
      </svg>
      <div className="md-nq-cap" key={stage}>{NQ_CAPTIONS[stage]}</div>
    </div>
  );
}

// Registry keyed by the public module names; `host` is the label shown in
// the frame's chrome bar (derived hosts get ugly for compound names).
const DEMOS = {
  nQube: { Demo: NqubeDemo, host: 'nqube.nventr' },
  agentOS: { Demo: AgentOsDemo, host: 'runtime · agentOS' },
  'Agent IO': { Demo: AgentIoDemo, host: 'agent-io.nventr' },
  'nventr Agent': { Demo: NventrAgentDemo, host: 'embed · nventr agent' },
  Athena: { Demo: AthenaDemo, host: 'athena.nventr' },
  Listen: { Demo: ListenDemo, host: 'listen.nventr' },
  Reachout: { Demo: ReachoutDemo, host: 'reachout.nventr' },
  'Dev Console': { Demo: DevConsoleDemo, host: 'instance /dev' },
  DevOps: { Demo: CloudOpsDemo, host: 'devops.nventr' },
  IDP: { Demo: IdpDemo, host: 'idp.nventr' },
};

// Browser-chrome framed demo for one module; falls back to nothing if the
// module has no registered demo.
export default function ModuleDemo({ name }) {
  const entry = DEMOS[name];
  if (!entry) return null;
  const { Demo, host } = entry;
  return (
    <div className="md-frame">
      <div className="md-frame-bar">
        <i /><i /><i />
        <span>{host}</span>
      </div>
      <div className="md-frame-body">
        <Demo />
      </div>
    </div>
  );
}
